import { apiRequest } from '@/utils/api-request';
import * as Sentry from '@sentry/react';
import mime from 'mime';

const MULTIPART_THRESHOLD = 90 * 1024 * 1024; // > 90 MB → presigned S3 multipart
const DEFAULT_PART_SIZE = 8 * 1024 * 1024; // Matches BE MULTIPART.DEFAULT_PART_SIZE

// Extension-based MIME overrides for types not in the `mime` package's default
// db — most notably Apache Parquet. Add more as the BE MULTIPART_UPLOAD_MIME_TYPES
// list evolves.
const EXTENSION_MIME_OVERRIDES: Record<string, string> = {
  parquet: 'application/vnd.apache.parquet',
};

function deriveContentType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext) {
    const override = EXTENSION_MIME_OVERRIDES[ext];
    if (override) return override;
    const mimeType = mime.getType(ext);
    if (mimeType) return mimeType;
  }
  return 'application/octet-stream';
}

interface MultipartPartUrl {
  part_number: number;
  url: string;
  expires_in: number;
}

interface MultipartInitResponse {
  upload_id: string;
  s3_key: string;
  s3_bucket: string;
  part_size: number;
  part_count: number;
  total_size: number;
  expires_in: number;
  part_urls: MultipartPartUrl[];
}

interface MultipartCompleteResponse {
  upload_id: string;
  completion_status: string;
  s3_bucket: string;
  s3_key: string;
  part_count: number;
  message?: string;
}

interface MultipartStatusResponse {
  upload_id: string;
  completion_status?: string;
  status?: string;
  s3_bucket?: string;
  s3_key?: string;
  filename?: string;
  total_size?: number;
  dataset_id?: string;
  glue_table_name?: string;
  glue_database_name?: string;
  parquet_schema_preview?: unknown;
  completion_steps_log?: unknown[];
  failure_reason?: { step?: string; exc_str?: string; at?: string };
  // Unstructured: surfaced from the BE register step so the FE can drive the
  // per-file ingestion-status poll (collection-page.tsx gates polling on
  // tags.ingestion_method === 'celery').
  celery_task_id?: string;
  ingestion_method?: string;
  ingestion_status?: string;
}

export interface MultipartUploadResult {
  filename: string;
  s3_key: string;
  s3_bucket: string;
  total_size: number;
  content_type: string;
  upload_id: string;
  dataset_id?: string;
  glue_table_name?: string;
  glue_database_name?: string;
  celery_task_id?: string;
  ingestion_method?: string;
  ingestion_status?: string;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

export interface ShouldUseMultipartOptions {
  dataType?: 'structured' | 'unstructured' | null;
  multipartMimeTypes?: string[];
}

export function shouldUseMultipartUpload(
  file: File,
  options: ShouldUseMultipartOptions = {},
): boolean {
  if (options.dataType === 'unstructured') {
    // Unstructured: always multipart, regardless of MIME or size.
    return true;
  }
  // Structured: route via multipart only when size exceeds the threshold
  // AND (if an allowlist is supplied) the MIME is multipart-eligible.
  // When no allowlist is provided, fall back to pure size gate for
  // backwards compatibility.
  if (file.size <= MULTIPART_THRESHOLD) return false;
  if (!options.multipartMimeTypes || options.multipartMimeTypes.length === 0) {
    return true;
  }
  const contentType = file.type || '';
  return options.multipartMimeTypes.includes(contentType);
}

export interface MultipartUploadOptions {
  fieldOverrides?: Record<string, unknown>;
  externalId?: string;
}

async function initMultipart(
  orgId: string,
  collectionId: string,
  file: File,
  options: MultipartUploadOptions = {},
): Promise<MultipartInitResponse> {
  const response = await apiRequest<MultipartInitResponse>({
    url: `/uploads/${orgId}/${collectionId}/multipart/init`,
    method: 'POST',
    target: 'platform',
    body: {
      filename: file.name,
      total_size: file.size,
      part_size: DEFAULT_PART_SIZE,
      content_type: deriveContentType(file),
      ...(options.fieldOverrides
        ? { field_overrides: options.fieldOverrides }
        : {}),
      ...(options.externalId ? { external_id: options.externalId } : {}),
    },
  });

  if (response?.detail || !response?.data?.upload_id) {
    throw new Error(response?.detail || 'Failed to initiate multipart upload');
  }
  return response.data;
}

async function uploadPart(
  url: string,
  blob: Blob,
  signal?: AbortSignal,
): Promise<string> {
  // Presigned S3 PUT — must NOT go through /proxy/platform and must NOT include
  // our Authorization header. Bypass apiRequest entirely.
  const res = await fetch(url, {
    method: 'PUT',
    body: blob,
    signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `S3 part PUT failed: ${res.status} ${res.statusText}${text ? ` — ${text.slice(0, 200)}` : ''}`,
    );
  }
  const etag = res.headers.get('ETag') || res.headers.get('etag');
  if (!etag) {
    throw new Error('S3 part response missing ETag header');
  }
  return etag;
}

async function completeMultipart(
  orgId: string,
  collectionId: string,
  uploadId: string,
  parts: { part_number: number; etag: string }[],
): Promise<MultipartCompleteResponse> {
  const response = await apiRequest<MultipartCompleteResponse>({
    url: `/uploads/${orgId}/${collectionId}/multipart/${uploadId}/complete`,
    method: 'POST',
    target: 'platform',
    body: { parts },
  });
  if (response?.detail || !response?.data) {
    throw new Error(response?.detail || 'Failed to complete multipart upload');
  }
  return response.data;
}

async function fetchMultipartStatus(
  orgId: string,
  collectionId: string,
  uploadId: string,
): Promise<MultipartStatusResponse> {
  const response = await apiRequest<MultipartStatusResponse>({
    url: `/uploads/${orgId}/${collectionId}/multipart/${uploadId}`,
    method: 'GET',
    target: 'platform',
  });
  if (response?.detail || !response?.data) {
    throw new Error(
      response?.detail || 'Failed to fetch multipart upload status',
    );
  }
  return response.data;
}

async function pollCompletionStatus(
  orgId: string,
  collectionId: string,
  uploadId: string,
  signal?: AbortSignal,
): Promise<MultipartStatusResponse> {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
    const body = await fetchMultipartStatus(orgId, collectionId, uploadId);
    if (body.completion_status === 'registered') {
      return body;
    }
    if (body.completion_status === 'failed') {
      const { step = 'unknown', exc_str = 'no details' } =
        body.failure_reason ?? {};
      throw new Error(`upload failed at step=${step}: ${exc_str}`);
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
  throw new Error(
    'upload completed but processing took longer than expected; refresh to see the dataset when it appears.',
  );
}

async function abortMultipart(
  orgId: string,
  collectionId: string,
  uploadId: string,
): Promise<void> {
  try {
    await apiRequest({
      url: `/uploads/${orgId}/${collectionId}/multipart/${uploadId}`,
      method: 'DELETE',
      target: 'platform',
    });
  } catch (err) {
    // Abort is best-effort — swallow but capture for visibility.
    Sentry.captureException(err, {
      extra: { context: 'multipart-abort', uploadId, collectionId },
    });
  }
}

export async function multipartUpload(
  file: File,
  orgId: string,
  collectionId: string,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
  options: MultipartUploadOptions = {},
): Promise<MultipartUploadResult> {
  const init = await initMultipart(orgId, collectionId, file, options);
  const { upload_id, part_size, part_urls } = init;

  try {
    const parts: { part_number: number; etag: string }[] = [];
    for (const partUrl of part_urls) {
      const start = (partUrl.part_number - 1) * part_size;
      const end = Math.min(start + part_size, file.size);
      const blob = file.slice(start, end);
      const etag = await uploadPart(partUrl.url, blob, signal);
      parts.push({ part_number: partUrl.part_number, etag });
      onProgress?.(Math.round((parts.length / part_urls.length) * 95));
    }

    const complete = await completeMultipart(
      orgId,
      collectionId,
      upload_id,
      parts,
    );
    // /complete returns 202 immediately; poll until the background
    // pipeline reaches a terminal state.
    const final = await pollCompletionStatus(
      orgId,
      collectionId,
      complete.upload_id,
      signal,
    );
    onProgress?.(100);

    return {
      filename: file.name,
      s3_key: complete.s3_key,
      s3_bucket: complete.s3_bucket,
      total_size: final.total_size ?? file.size,
      content_type: deriveContentType(file),
      upload_id: complete.upload_id,
      dataset_id: final.dataset_id,
      glue_table_name: final.glue_table_name,
      glue_database_name: final.glue_database_name,
      celery_task_id: final.celery_task_id,
      ingestion_method: final.ingestion_method,
      ingestion_status: final.ingestion_status,
    };
  } catch (err) {
    // Best-effort cleanup so S3 doesn't retain orphaned parts.
    await abortMultipart(orgId, collectionId, upload_id);
    Sentry.captureException(err, {
      extra: {
        context: 'multipart-upload',
        filename: file.name,
        fileSize: file.size,
        collectionId,
        uploadId: upload_id,
      },
    });
    throw err;
  }
}
